import { useState } from "react";
import { makeStyles, shorthands } from "@fluentui/react-components";
import {
    gesellschafterlisteAbrufen,
    registerAbdruckAbrufen,
    registerBeteiligteAbrufen,
    type RegisterAbdruck,
    type RegisterBeteiligteErgebnis,
} from "../../services/firmendatenService";
import { useDocumentViewer } from "../documents/useDocumentViewer";
import { RegisterBeteiligteDialog } from "./RegisterBeteiligteDialog";
import { VerflechtungDialog } from "./VerflechtungDialog";
import { BeteiligungsrechercheDialog } from "./BeteiligungsrechercheDialog";
import { GesellschaftsvertragDialog } from "./GesellschaftsvertragDialog";
import { RegisterhistorieDialog } from "./RegisterhistorieDialog";
import { UstIdDialog } from "./UstIdDialog";
import { BranchenvorschlagDialog } from "./BranchenvorschlagDialog";
import { GroessenklasseDialog } from "./GroessenklasseDialog";

/**
 * Die beiden Registeraktionen unter den Steuer- & Rechtsdaten einer Firma:
 * den aktuellen Abdruck holen und öffnen, und die eingetragenen Personen als
 * Kontakte übernehmen.
 *
 * Ein Link ins Portal wäre für den Abdruck keine Alternative:
 * handelsregister.de hat für Registerblätter keine aufrufbare URL. Und weil ein
 * Abdruck eine Momentaufnahme ist, steht sein Abrufdatum daneben — mit der
 * Möglichkeit, einen frischen zu ziehen.
 */

// Griffel akzeptiert in makeStyles keine CSS-Shorthands (border/padding/gap …)
// — dafür gibt es `shorthands.*`. Longhands wie transitionDuration bleiben direkt.
const useStyles = makeStyles({
    /**
     * Die Aktionen stehen in Gruppen statt in einer Reihe. Elf Schaltflächen
     * nebeneinander sind fachlich alle berechtigt und als Bedienoberfläche
     * trotzdem keine — nach der Herkunft geordnet ist sofort zu sehen, was ein
     * Registerabruf kostet und was nur gerechnet wird.
     */
    gruppen: { display: "flex", flexDirection: "column", ...shorthands.gap("12px") },
    gruppe: { display: "flex", flexDirection: "column", ...shorthands.gap("6px") },
    gruppenTitel: {
        fontSize: "11px",
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "#8b7a86",
    },
    leiste: {
        display: "flex",
        alignItems: "center",
        ...shorthands.gap("8px"),
        flexWrap: "wrap",
    },
    knopf: {
        display: "inline-flex",
        alignItems: "center",
        ...shorthands.gap("8px"),
        ...shorthands.padding("9px", "14px"),
        ...shorthands.borderRadius("9px"),
        ...shorthands.border("1.5px", "solid", "#ddd3dc"),
        backgroundColor: "#fff",
        color: "#7d2169",
        fontFamily: '"Hanken Grotesk", sans-serif',
        fontSize: "12.5px",
        fontWeight: 700,
        lineHeight: 1,
        cursor: "pointer",
        whiteSpace: "nowrap",
        transitionDuration: "130ms",
        ":hover": { ...shorthands.borderColor("#cd8fbd"), backgroundColor: "#f8eef5" },
        ":disabled": { color: "#a596a1", cursor: "default", backgroundColor: "#f6f3f5" },
    },
    hinweis: { fontSize: "12px", color: "#6b5a66" },
    verweis: {
        ...shorthands.borderWidth("0"),
        ...shorthands.padding("0"),
        backgroundColor: "transparent",
        color: "#7d2169",
        fontFamily: "inherit",
        fontSize: "inherit",
        cursor: "pointer",
        textDecorationLine: "underline",
    },
    fehler: { fontSize: "12px", color: "#b42318", marginTop: "6px" },
});

export function HandelsregisterAbdruck({
    contactId,
    onReload,
    onFelder,
    ustId,
    taetigkeit,
    onBranche,
}: {
    contactId: number;
    /** Nach übernommenen Personen: Detailansicht neu laden (neue Verbindungen). */
    onReload?: () => void;
    /**
     * Werte aus dem Gesellschaftsvertrag in die Maske schreiben. Gespeichert
     * wird über den normalen Speichern-Knopf — der Vertrag schreibt nichts
     * an der Sachbearbeitung vorbei in die Datenbank.
     */
    onFelder?: (werte: Array<{ feld: string; wert: string }>) => void;
    /**
     * Die USt-IdNr. aus der Maske. Sie geht der gespeicherten vor: Geprüft
     * werden soll, was gerade im Feld steht — auch ungespeichert.
     */
    ustId?: string | null;
    /** Die Tätigkeitsbeschreibung aus der Maske — Grundlage des Branchenvorschlags. */
    taetigkeit?: string | null;
    /** Übernimmt eine vorgeschlagene Einordnung als Hauptbranche. */
    onBranche?: (treffer: { id: number; pfad: string }) => void;
}) {
    const s = useStyles();
    const openViewer = useDocumentViewer();
    const [laden, setLaden] = useState<
        false | "oeffnen" | "neu" | "personen" | "liste" | "gruender"
    >(false);
    /**
     * Zweite Stufe: Die Liste liegt in der Akte, die Texterkennung läuft noch.
     * Der Knopf dreht weiter — diese Zeile sagt, worauf gewartet wird, damit
     * eine halbe Minute Stille nicht wie ein Hänger aussieht.
     */
    const [erkennung, setErkennung] = useState(false);
    const [verflechtungOffen, setVerflechtungOffen] = useState(false);
    const [rechercheOffen, setRechercheOffen] = useState(false);
    const [vertragOffen, setVertragOffen] = useState(false);
    const [historieOffen, setHistorieOffen] = useState(false);
    const [ustIdOffen, setUstIdOffen] = useState(false);
    const [brancheOffen, setBrancheOffen] = useState(false);
    const [groesseOffen, setGroesseOffen] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);
    const [abdruck, setAbdruck] = useState<RegisterAbdruck | null>(null);
    const [personen, setPersonen] = useState<RegisterBeteiligteErgebnis | null>(null);

    const holen = async (neu: boolean) => {
        setLaden(neu ? "neu" : "oeffnen");
        setFehler(null);
        try {
            const a = await registerAbdruckAbrufen(contactId, neu);
            setAbdruck(a);
            openViewer({ id: a.documentId, fileName: a.fileName, mimeType: a.mimeType });
        } catch (e) {
            setFehler(e instanceof Error ? e.message : String(e));
        } finally {
            setLaden(false);
        }
    };

    const personenHolen = async () => {
        setLaden("personen");
        setFehler(null);
        try {
            const r = await registerBeteiligteAbrufen(contactId);
            setPersonen(r);
            // Derselbe Portalvorgang hat den Abdruck mitgebracht und abgelegt.
            // Ihn hier zu übernehmen ist keine Kosmetik: sonst steht daneben
            // weiter „noch kein Abdruck", und der nächste Klick holt einen
            // zweiten — für dasselbe Dokument, auf Kosten des Kontingents.
            if (r.abdruck) setAbdruck(r.abdruck);
        } catch (e) {
            setFehler(e instanceof Error ? e.message : String(e));
        } finally {
            setLaden(false);
        }
    };

    /**
     * Die Gesellschafter einer GmbH stehen NICHT im Registerauszug — nur in der
     * eingereichten Liste im Registerordner. Eigener Knopf, weil es ein eigener
     * Portalvorgang ist und nur für Gesellschaften mit Stammkapital etwas
     * hergibt: bei Personengesellschaften, Vereinen und AGs ist keine Liste
     * eingereicht, und das Portal deckelt die Abrufe je Stunde.
     */
    const gesellschafterHolen = async (waehle: "neueste" | "aelteste" = "neueste") => {
        setLaden(waehle === "aelteste" ? "gruender" : "liste");
        setFehler(null);
        try {
            const r = await gesellschafterlisteAbrufen(contactId, waehle, setErkennung);
            setPersonen({
                firma: {},
                treffer: null,
                beteiligte: r.vorschlaege,
                hinweise: r.hinweise,
                abdruck: null,
                dokument: {
                    documentId: r.documentId,
                    fileName: r.fileName,
                    mimeType: r.mimeType,
                },
            });
        } catch (e) {
            setFehler(e instanceof Error ? e.message : String(e));
        } finally {
            setLaden(false);
            setErkennung(false);
        }
    };

    const datum = abdruck?.abgerufenAm
        ? new Date(abdruck.abgerufenAm).toLocaleDateString("de-DE")
        : null;

    /**
     * Die Aktionen des Abschnitts, nach Herkunft geordnet.
     *
     * Datengetrieben statt elf handgeschriebener Blöcke — jede weitere Aktion
     * ist damit eine Zeile, und die Gruppierung sagt dem Anwender das
     * Wichtigste: Was kostet einen Registerabruf (und damit vom
     * Stundenkontingent), was wird nur gerechnet?
     */
    const gruppen: Array<{
        titel: string;
        aktionen: Array<{
            text: string;
            titel: string;
            onClick: () => void;
            /** Kennung, solange dieser Abruf läuft — dann die Ersatzbeschriftung. */
            laeuft?: typeof laden;
            ladeText?: string;
            /** Ohne Rückkanal in die Maske ist die Aktion sinnlos. */
            wenn?: boolean;
        }>;
    }> = [
        {
            titel: "Registerauszug",
            aktionen: [
                {
                    text: "Aktueller Abdruck öffnen",
                    titel: "Aktuellen Registerabdruck beim Handelsregister holen und öffnen",
                    onClick: () => holen(false),
                    laeuft: "oeffnen",
                    ladeText: "Wird beim Handelsregister geholt …",
                },
                {
                    text: "Historie & Gründung",
                    titel: "Gründung, Rechtsform und alle Eintragungen seit der ersten (chronologischer Abdruck)",
                    onClick: () => setHistorieOffen(true),
                    wenn: !!onFelder,
                },
                {
                    text: "Personen aus dem Register",
                    titel: "Geschäftsführer, Vorstand, Prokuristen und Gesellschafter aus dem Register übernehmen",
                    onClick: () => void personenHolen(),
                    laeuft: "personen",
                    ladeText: "Register wird gelesen …",
                },
            ],
        },
        {
            titel: "Registerordner",
            aktionen: [
                {
                    text: "Gesellschafter aus der Liste",
                    titel: "Die eingereichte Liste der Gesellschafter aus dem Registerordner holen und auswerten",
                    onClick: () => void gesellschafterHolen("neueste"),
                    laeuft: "liste",
                    ladeText: "Registerordner wird gelesen …",
                },
                {
                    text: "Gründungsgesellschafter",
                    titel: "Die ÄLTESTE eingereichte Gesellschafterliste — wer die Gesellschaft ursprünglich gehalten hat",
                    onClick: () => void gesellschafterHolen("aelteste"),
                    laeuft: "gruender",
                    ladeText: "Erste Liste wird gelesen …",
                },
                {
                    text: "Gesellschaftsvertrag auswerten",
                    titel: "Unternehmensgegenstand, Geschäftsjahr und Rolle im Verbund aus dem Gesellschaftsvertrag",
                    onClick: () => setVertragOffen(true),
                    wenn: !!onFelder,
                },
            ],
        },
        {
            titel: "Prüfen & einordnen",
            aktionen: [
                {
                    text: "USt-IdNr. prüfen",
                    titel: "Die USt-IdNr. beim europäischen Bestätigungsverfahren gegenprüfen — kostenfrei",
                    onClick: () => setUstIdOffen(true),
                    wenn: !!onFelder,
                },
                {
                    text: "Branche vorschlagen",
                    titel: "Aus dem Unternehmensgegenstand die passende Branche im Katalog vorschlagen",
                    onClick: () => setBrancheOffen(true),
                    wenn: !!onBranche,
                },
                {
                    text: "Größenklasse bestimmen",
                    titel: "Größenklasse nach § 267 HGB und die Prüfungspflicht daraus bestimmen",
                    onClick: () => setGroesseOffen(true),
                    wenn: !!onFelder,
                },
            ],
        },
        {
            titel: "Verflechtung",
            aktionen: [
                {
                    text: "Verflechtung ausbauen",
                    titel:
                        "Gesellschafter der Gesellschafter nachverfolgen — sammelt Vorschläge, " +
                        "angelegt wird erst nach dem Bestätigen",
                    onClick: () => setVerflechtungOffen(true),
                },
                {
                    text: "Weitere Beteiligungen suchen",
                    titel:
                        "An welchen anderen Gesellschaften ist diese Firma beteiligt? — Websuche; " +
                        "ein Treffer lässt sich danach im Register prüfen und übernehmen",
                    onClick: () => setRechercheOffen(true),
                },
            ],
        },
    ];

    return (
        <div className="fld span2">
            <div className={s.gruppen}>
                {gruppen.map((g) => {
                    const sichtbar = g.aktionen.filter((a) => a.wenn !== false);
                    if (!sichtbar.length) return null;
                    return (
                        <div key={g.titel} className={s.gruppe}>
                            <span className={s.gruppenTitel}>{g.titel}</span>
                            <div className={s.leiste}>
                                {sichtbar.map((a) => (
                                    <button
                                        key={a.text}
                                        type="button"
                                        className={s.knopf}
                                        disabled={laden !== false}
                                        onClick={a.onClick}
                                        title={a.titel}
                                    >
                                        {a.laeuft && laden === a.laeuft ? a.ladeText : a.text}
                                    </button>
                                ))}
                            </div>
                        </div>
                    );
                })}
                {datum && (
                    <span className={s.hinweis}>
                        Abdruck vom {datum}
                        {" · "}
                        <button
                            type="button"
                            className={s.verweis}
                            disabled={laden !== false}
                            onClick={() => holen(true)}
                        >
                            {laden === "neu" ? "wird geholt …" : "neu abrufen"}
                        </button>
                    </span>
                )}
            </div>
            {erkennung && (
                <div className={s.hinweis}>
                    Die Liste liegt in der Akte — die Texterkennung läuft. Bei einem Scan
                    dauert das etwa eine halbe Minute.
                </div>
            )}
            {fehler && <div className={s.fehler}>{fehler}</div>}
            {groesseOffen && onFelder && (
                <GroessenklasseDialog
                    contactId={contactId}
                    onClose={() => setGroesseOffen(false)}
                    onUebernehmen={onFelder}
                />
            )}
            {brancheOffen && onBranche && (
                <BranchenvorschlagDialog
                    contactId={contactId}
                    gegenstand={taetigkeit}
                    onClose={() => setBrancheOffen(false)}
                    onWaehlen={onBranche}
                />
            )}
            {ustIdOffen && onFelder && (
                <UstIdDialog
                    contactId={contactId}
                    nummer={ustId}
                    onClose={() => setUstIdOffen(false)}
                    onUebernehmen={onFelder}
                />
            )}
            {historieOffen && onFelder && (
                <RegisterhistorieDialog
                    contactId={contactId}
                    onClose={() => setHistorieOffen(false)}
                    onUebernehmen={onFelder}
                    onDokumentOeffnen={(d) =>
                        openViewer({ id: d.documentId, fileName: d.fileName, mimeType: d.mimeType })
                    }
                />
            )}
            {vertragOffen && onFelder && (
                <GesellschaftsvertragDialog
                    contactId={contactId}
                    onClose={() => setVertragOffen(false)}
                    onUebernehmen={onFelder}
                    onDokumentOeffnen={(d) =>
                        openViewer({ id: d.documentId, fileName: d.fileName, mimeType: d.mimeType })
                    }
                />
            )}
            {rechercheOffen && (
                <BeteiligungsrechercheDialog
                    contactId={contactId}
                    onClose={() => setRechercheOffen(false)}
                    onAngelegt={() => onReload?.()}
                />
            )}
            {verflechtungOffen && (
                <VerflechtungDialog
                    contactId={contactId}
                    onClose={() => setVerflechtungOffen(false)}
                    onFertig={() => onReload?.()}
                />
            )}
            {personen && (
                <RegisterBeteiligteDialog
                    contactId={contactId}
                    daten={personen}
                    onClose={() => setPersonen(null)}
                    onUebernommen={() => onReload?.()}
                    onDokumentOeffnen={
                        personen.dokument?.documentId
                            ? () =>
                                  openViewer({
                                      id: personen.dokument!.documentId!,
                                      fileName: personen.dokument!.fileName ?? "Dokument",
                                      mimeType: personen.dokument!.mimeType,
                                  })
                            : undefined
                    }
                />
            )}
        </div>
    );
}
